import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Phone,
  Email,
  LocationOn,
  Business,
  AttachMoney,
  CalendarToday,
  Description,
  Save,
  Cancel,
} from '@mui/icons-material';
import { leadService } from '../../services/lead';
import { getAssociates } from '../../services/associate';
import { globalMasterService } from '../../services/stateAPI';

const AddLead = () => {
  const history = useHistory();
  const location = useLocation();
  const customer = location.state?.customer;

  // Extract customer data with proper fallbacks for nested structures
  const getCustomerData = () => {
    if (!customer) return {};
    
    // Handle nested structures
    const basicDetails = customer?.basicDetails || {};
    const personalDetails = customer?.personalDetails || {};
    const otherDetails = customer?.otherDetails || {};
    
    // Handle customerId - could be customerNo or _id
    const customerId = customer?.customerId 
      || (customer?.customerNo ? `CUST${String(customer.customerNo).padStart(4, '0')}` : '')
      || customer?._id || '';
    
    return {
      customerId,
      customerName: customer?.name || '',
      mobile: customer?.phone || customer?.mobile || '',
      whatsapp: customer?.whatsapp || customer?.phone || customer?.mobile || '',
      email: customer?.email || '',
      company: customer?.company || customer?.companyName || '',
      designation: customer?.profession || '',
      addressLine: otherDetails?.address || '',
      city: customer?.city || '',
      state: customer?.state || '',
      assignedTo: customer?.assignedTo || '',
      associateName: customer?.associateName || '',
    };
  };

  const customerData = getCustomerData();

  const [formData, setFormData] = useState({
    leadId: `LEAD${Date.now()}`,
    customerId: customerData.customerId,
    customerName: customerData.customerName,
    leadSource: '',
    leadStatus: 'New',
    priority: 'Medium',
    budget: '',
    interestedIn: '',
    location: '',
    expectedDate: '',
    assignedTo: customerData.assignedTo || '',
    followUpDate: '',
    remark: '',
    contactPerson: customerData.customerName,
    contactNumber: customerData.mobile,
    whatsapp: customerData.whatsapp,
    email: customerData.email,
    company: customerData.company,
    designation: customerData.designation,
    addressLine: customerData.addressLine,
    city: customerData.city,
    state: customerData.state,
    projectInterestedIn: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [errors, setErrors] = useState({});
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loadingPropertyTypes, setLoadingPropertyTypes] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[4-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const validate = () => {
    const newErrors = {};

    // Required fields
    if (!formData.leadSource?.trim()) {
      newErrors.leadSource = 'Lead Source is required';
    }

    if (!formData.propertyTypeId?.trim()) {
      newErrors.interestedIn = 'Property Type is required';
    }

    if (!formData.assignedTo?.trim()) {
      newErrors.assignedTo = 'Assigned To is required';
    }

    if (!formData.addressLine?.trim()) {
      newErrors.addressLine = 'Address is required';
    }

    // Email validation
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // WhatsApp validation
    if (formData.whatsapp && !validatePhone(formData.whatsapp)) {
      newErrors.whatsapp = 'WhatsApp number must be 10 digits and cannot start with 0, 1, 2, or 3';
    }

    // Contact number validation
    if (formData.contactNumber && !validatePhone(formData.contactNumber)) {
      newErrors.contactNumber = 'Contact number must be 10 digits and cannot start with 0, 1, 2, or 3';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch associates on component mount
  useEffect(() => {
    const fetchAssociates = async () => {
      setLoadingAssociates(true);
      try {
        const response = await getAssociates({ page: 1, limit: 10 });
        if (response.status && Array.isArray(response.data)) {
          const mappedAssociates = response.data.map((associate) => ({
            id: associate._id,
            _id: associate._id,
            name: associate.name || '',
            phone: associate.phone || '',
            displayLabel: `${associate.name || ''} - ${associate.phone || ''}`,
          }));
          setAssociates(mappedAssociates);
          
          // If customer has assignedTo, find and set selected associate
          if (customerData.assignedTo) {
            const foundAssociate = mappedAssociates.find(a => a._id === customerData.assignedTo);
            if (foundAssociate) {
              setSelectedAssociate(foundAssociate);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load associates:', error);
        setAssociates([]);
      } finally {
        setLoadingAssociates(false);
      }
    };
    
    fetchAssociates();
  }, [customerData.assignedTo]);

  // Fetch budget options on component mount
  useEffect(() => {
    const fetchBudgets = async () => {
      setLoadingBudgets(true);
      try {
        const budgets = await globalMasterService.getBudgets();
        setBudgetOptions(budgets);
      } catch (error) {
        console.error('Failed to load budgets:', error);
        setBudgetOptions([]);
      } finally {
        setLoadingBudgets(false);
      }
    };
    
    fetchBudgets();
  }, []);

  // Fetch property types from global master
  useEffect(() => {
    const loadPropertyTypes = async () => {
      setLoadingPropertyTypes(true);
      try {
        const types = await globalMasterService.getProjectTypesWithIds();
        setPropertyTypes(types);
      } catch (error) {
        console.error('Failed to load property types:', error);
        setPropertyTypes([]);
      } finally {
        setLoadingPropertyTypes(false);
      }
    };

    loadPropertyTypes();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validate form before submitting
    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      // Map form data to API structure
      const leadData = {
        interestedIn: formData.interestedIn,
        propertyTypeId: formData.propertyTypeId || '',
        projectInterestedIn: formData.projectInterestedIn,
        source: formData.leadSource,
        stage: "Lead",
        status: formData.leadStatus,
        address: {
          addressLine: formData.addressLine,
          city: formData.city,
          state: formData.state
        },
        followUp: {
          nextFollowUpDate: formData.followUpDate || null,
          lastFollowUpRemark: formData.remark,
          lastFollowUpDate: new Date().toISOString().split('T')[0]
        },
        remarks: {
          remark1: formData.remark,
          remark2: "",
          generalRemark: formData.remark
        },
        associateId: formData.assignedTo || "", // Use assignedTo from customer data
        createdBy: "a1 Enterprises Pvt. Ltd",
        updatedBy: "Admin",
        isDeleted: false,
        isActive: true,
        customerDetails: {
          customerExist: !!(customer && (customer._id || customer.id)),
          customerId: customer?._id || customer?.id || customerData.customerId || '',
          name: formData.customerName,
          email: formData.email,
          phone: formData.contactNumber,
          whatsapp: formData.whatsapp,
          company: formData.company,
          designation: formData.designation
        }
      };

      await leadService.createLead(leadData);
      setSuccess(true);

      // Redirect after a short delay to show success message
      setTimeout(() => {
        history.goBack();
      }, 1500);

    } catch (err) {
      setError(err.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <IconButton onClick={() => history.goBack()} sx={{ color: '#536DFE' }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              Add Lead
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new lead for {customerData.customerName || customer?.name || 'customer'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Customer Info Card */}
      {customer && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {customerData.customerName || customer?.name || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ color: '#25D366' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Mobile</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {customerData.mobile || customer?.mobile || customer?.phone || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ color: '#666' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {customerData.email || customer?.email || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ color: '#666' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {customerData.city || customer?.city || '-'}, {customerData.state || customer?.state || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Lead created successfully!
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lead Form */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#000' }}>
            Lead Details
          </Typography>

          <Grid container spacing={3}>
            {/* Lead Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#536DFE' }}>
                Lead Information
              </Typography>
            </Grid>

            {/* <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lead ID"
                value={formData.leadId}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid> */}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer ID"
                value={formData.customerId}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!errors.leadSource}>
                <InputLabel>Lead Source</InputLabel>
                <Select
                  value={formData.leadSource}
                  onChange={(e) => handleInputChange('leadSource', e.target.value)}
                  label="Lead Source"
                >
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Advertisement">Advertisement</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Cold Call">Cold Call</MenuItem>
                  <MenuItem value="Walk-in">Walk-in</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              {errors.leadSource && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.leadSource}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Lead Status</InputLabel>
                <Select
                  value={formData.leadStatus}
                  onChange={(e) => handleInputChange('leadStatus', e.target.value)}
                  label="Lead Status"
                >
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="Contacted">Contacted</MenuItem>
                  <MenuItem value="Qualified">Qualified</MenuItem>
                  <MenuItem value="Proposal">Proposal</MenuItem>
                  <MenuItem value="Negotiation">Negotiation</MenuItem>
                  <MenuItem value="Closed Won">Closed Won</MenuItem>
                  <MenuItem value="Closed Lost">Closed Lost</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Budget</InputLabel>
                <Select
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                  label="Budget"
                  disabled={loadingBudgets}
                >
                  {loadingBudgets ? (
                    <MenuItem value="" disabled>
                      Loading...
                    </MenuItem>
                  ) : budgetOptions.length > 0 ? (
                    budgetOptions.map((budget) => (
                      <MenuItem key={budget} value={budget}>
                        {budget}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No budgets available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                value={formData.whatsapp}
                onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="WhatsApp contact number"
                error={!!errors.whatsapp}
                helperText={errors.whatsapp || "Enter 10 digit WhatsApp number (optional)"}
                inputProps={{
                  maxLength: 10,
                  pattern: '[4-9][0-9]{9}',
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Email address"
                error={!!errors.email}
                helperText={errors.email || "Enter valid email address (optional)"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address"
                value={formData.addressLine}
                onChange={(e) => handleInputChange('addressLine', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Full address"
                required
                error={!!errors.addressLine}
                helperText={errors.addressLine}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="State name"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="City name"
              />
            </Grid>

          

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!errors.interestedIn}>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={formData.propertyTypeId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const match = propertyTypes.find((p) => p.id === selectedId);
                    handleInputChange('propertyTypeId', selectedId);
                    handleInputChange('interestedIn', match?.label || '');
                  }}
                  label="Property Type"
                  disabled={loadingPropertyTypes}
                >
                  <MenuItem value="">
                    <em>{loadingPropertyTypes ? 'Loading...' : 'Select Property Type'}</em>
                  </MenuItem>
                  {propertyTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.interestedIn && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.interestedIn}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Preferred Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="e.g., Andheri, Bandra"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Purchase Date"
                type="date"
                value={formData.expectedDate}
                onChange={(e) => handleInputChange('expectedDate', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={associates}
                getOptionLabel={(option) => option.displayLabel || `${option.name} - ${option.phone}`}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  if (newValue) {
                    handleInputChange('assignedTo', newValue._id || newValue.id || '');
                  } else {
                    handleInputChange('assignedTo', '');
                  }
                }}
                loading={loadingAssociates}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned To"
                    variant="outlined"
                    placeholder="Select Associate"
                    required
                    error={!!errors.assignedTo}
                    helperText={errors.assignedTo}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {option.phone}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Follow-up Date"
                type="date"
                value={formData.followUpDate}
                onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remark"
                value={formData.remark}
                onChange={(e) => handleInputChange('remark', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={3}
                placeholder="Additional notes about the lead..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                backgroundColor: '#536DFE',
                '&:hover': {
                  backgroundColor: '#3f51b5',
                }
              }}
            >
              {loading ? 'Saving...' : 'Save Lead'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddLead;
